// =====================================================================================
// INVRT Radar — web console — "Find people at this company": the bridge money path
//
// POST /bridge/company-to-people is ONE route with TWO phases (api/routes/bridge.ts):
//
//   without quote_id → FREE preview: the decision-makers at the domain (no contact detail),
//                      the count the suppression list removed, and the exact price
//   with quote_id    → the REVEAL, through the ordinary prospecting money path
//
// The shape of this hook mirrors hooks/useRevealFlow.ts deliberately, because the same
// properties have to hold here:
//
//   idle → previewing → awaiting_approval → revealing → done
//
//   - Phase one never spends. The only way to reach phase two is `approve()`, which refuses
//     outright unless a quote is sitting in `awaiting_approval`.
//   - The idempotency key is minted ONCE, at approval, and reused for every retry of that
//     same approval, so a retried call replays rather than charging twice.
//   - A refused execution keeps the quote on screen so the same approval can be retried
//     under the same key; a quote_payload_mismatch (the people at the company changed) is
//     answered by previewing again, which the error copy already says.
//   - After a reveal the ledger moved and new rows exist, so the balance, the ledger and
//     every contacts read are invalidated rather than trusted.
// =====================================================================================

import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { newIdempotencyKey } from "@/lib/utils";
import type { Numeric, RevealResponse } from "@/lib/types";

/** The contract's cap on a bridge request; the drawer asks for up to this many. */
export const BRIDGE_MAX_PEOPLE = 10;

/** One free preview row: never carries contact detail (that is what phase two is paid for). */
export interface BridgeCandidate {
  provider_ref: string;
  company_name?: string;
  title?: string;
  location?: string;
}

/** The quote as the bridge echoes it from /leads/quote-reveal. `credits` is the database's figure: format it, never compute with it. */
export interface BridgeQuote {
  quote_id: string;
  unit_count: number;
  credits: Numeric;
  expires_at: string;
}

export type BridgePreviewToken = "quoted" | "no_candidates" | "all_candidates_suppressed";

/** Phase one's response (bridge.ts PreviewResponseSchema). */
export interface BridgePreviewResponse {
  company: { domain: string };
  candidates: BridgeCandidate[];
  suppressed_count: number;
  quote: BridgeQuote | null;
  token: BridgePreviewToken;
}

/** Phase two's response (bridge.ts RevealResponseSchema): /leads/reveal's own result plus the bridge's framing. */
export interface BridgeRevealResponse extends RevealResponse {
  company: { domain: string };
  bridge: { candidate_count: number; suppressed_count: number; max_people: number };
  token: "revealed";
}

export type BridgePhase =
  | "idle"
  | "previewing"
  | "awaiting_approval"
  | "nothing_to_reveal"
  | "revealing"
  | "done"
  | "failed";

export interface CompanyBridgeState {
  phase: BridgePhase;
  domain: string | null;
  preview: BridgePreviewResponse | null;
  result: BridgeRevealResponse | null;
  error: unknown;
}

export interface CompanyBridge extends CompanyBridgeState {
  /** Phase one. Free: no ledger movement of any kind. */
  previewFor: (domain: string) => Promise<BridgePreviewResponse | null>;
  /** THE SPEND. Only reachable from `awaiting_approval`, only by a human action. */
  approve: () => Promise<BridgeRevealResponse | null>;
  /** Abandons an un-approved quote. A quote left to expire costs nothing. */
  cancel: () => void;
  reset: () => void;
}

const INITIAL: CompanyBridgeState = {
  phase: "idle",
  domain: null,
  preview: null,
  result: null,
  error: null,
};

export function useCompanyBridge(): CompanyBridge {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const [state, setState] = useState<CompanyBridgeState>(INITIAL);
  // Minted at approval and held for the life of that approval — see the header.
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);

  const reset = useCallback(() => {
    setState(INITIAL);
    setIdempotencyKey(null);
  }, []);

  const previewFor = useCallback(
    async (domain: string): Promise<BridgePreviewResponse | null> => {
      const trimmed = domain.trim();
      if (trimmed === "") return null;

      setState({ phase: "previewing", domain: trimmed, preview: null, result: null, error: null });
      setIdempotencyKey(null);

      try {
        const preview = await api.post<BridgePreviewResponse>("/bridge/company-to-people", {
          company: { domain: trimmed },
          people: { max_people: BRIDGE_MAX_PEOPLE },
        });
        setState({
          phase: preview.token === "quoted" && preview.quote ? "awaiting_approval" : "nothing_to_reveal",
          domain: trimmed,
          preview,
          result: null,
          error: null,
        });
        return preview;
      } catch (error) {
        setState({ phase: "failed", domain: trimmed, preview: null, result: null, error });
        return null;
      }
    },
    [api],
  );

  const approve = useCallback(async (): Promise<BridgeRevealResponse | null> => {
    const { preview, domain } = state;
    const quote = preview?.quote ?? null;
    // The gate. Nothing else in this hook can reach phase two.
    if (!quote || !domain || state.phase !== "awaiting_approval") return null;

    const key = idempotencyKey ?? newIdempotencyKey("radar-web-bridge");
    setIdempotencyKey(key);
    setState((current) => ({ ...current, phase: "revealing", error: null }));

    try {
      // The SAME company and people block as phase one, so the server re-derives the same
      // candidate set the quote committed to; a changed set is refused, never charged.
      const result = await api.post<BridgeRevealResponse>("/bridge/company-to-people", {
        company: { domain },
        people: { max_people: BRIDGE_MAX_PEOPLE },
        quote_id: quote.quote_id,
        idempotency_key: key,
      });
      setState({ phase: "done", domain, preview, result, error: null });
      void queryClient.invalidateQueries({ queryKey: ["balance", orgId] });
      void queryClient.invalidateQueries({ queryKey: ["ledger", orgId] });
      void queryClient.invalidateQueries({ queryKey: ["contacts", orgId] });
      return result;
    } catch (error) {
      // Keep the quote: a transient fault is worth retrying under the SAME key.
      setState({ phase: "awaiting_approval", domain, preview, result: null, error });
      return null;
    }
  }, [api, idempotencyKey, orgId, queryClient, state]);

  return useMemo(
    () => ({ ...state, previewFor, approve, cancel: reset, reset }),
    [state, previewFor, approve, reset],
  );
}
